import React, { ChangeEvent, useEffect, useState } from 'react';
import { stringify } from 'query-string';
import {
  append,
  equals,
  identity,
  ifElse,
  includes,
  isEmpty,
  map,
  mapObjIndexed,
  pick,
  pipe,
  without,
  toString,
  propOr,
  is,
  always,
  find,
  propEq,
  filter,
  isNil,
  complement,
  omit,
  all,
  values,
} from 'ramda';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { AnimatePresence } from 'framer-motion';

import { FilterItem, FilterOption } from '@definitions/filters.types';
import {
  DEFAULT_FILTER_NAMES,
  FILTER_CONTAINER_TEST_ID,
  FILTER_DOMINANT_COLORS,
  FILTER_NAME,
  FILTER_ORDER,
  MAX_PRICE,
  MIN_PRICE,
} from '@components/headerFilter/headerFilter.constants';
import { ROUTE } from '@utils/constants';
import { useArtworksRoute } from '@hooks/useArtworks/useArtworks';
import { createArrayIfNot, mapArray } from '@utils/helpers';
import { BUTTON_SIZE } from '@components/form';
import { renderWhenTrue } from '@utils/rendering';
import { FocusTrap } from '@components/common';
import { InputField } from '@components/inputField';

import {
  ActionButton,
  CloseButton,
  Color,
  Container,
  FilterActions,
  FilterItemContainer,
  FilterItemMobileContainer,
  FilterMobileName,
  FilterOptionsContainer,
  FilterOptionsMobileContainer,
  FiltersContainer,
  FilterSpacer,
  FilterTitle,
  Option,
  OptionColor,
  OptionText,
  TransformableContainer,
  DesktopFiltersContainer,
  MobileFiltersContainer,
  FilterName,
  FilterColorsContainer,
  PriceInputs,
} from './headerFilter.styles';

export interface HeaderFilterProps {
  options?: FilterItem[];
  open: boolean;
  onClose?: () => any;
}

interface SelectedOptions {
  inputPrice?: string;
  [key: string]: string | string[] | undefined;
}

export const HeaderFilter = ({ options = [], open, onClose = () => ({}) }: HeaderFilterProps) => {
  const handleOnClose = () => onClose();
  const router = useRouter();
  const { isGroupingRoute } = useArtworksRoute();
  const intl = useIntl();

  const closeLabel = intl.formatMessage({ id: 'headerFilter.close', defaultMessage: 'Close filter dropdown' });
  const minLabel = intl.formatMessage({ id: 'headerFilter.min', defaultMessage: 'Min' });
  const maxLabel = intl.formatMessage({ id: 'headerFilter.max', defaultMessage: 'Max' });
  const sanitizeOptions = pick(FILTER_ORDER);
  const orderedOptions = filter(
    complement(isNil),
    map((filter) => find(propEq('name', filter), options || []), FILTER_ORDER)
  ) as FilterItem[];

  const [selectedOptions, setSelectedOptions] = useState<{
    inputPrice?: string;
    [key: string]: string | string[] | undefined;
  }>({});
  const [openOption, setOpenOption] = useState('');
  const isFilterOpen = equals(openOption);

  useEffect(() => {
    const { inputPrice } = router.query as { inputPrice: string };
    const queryOptions = mapObjIndexed(mapArray, sanitizeOptions(router.query));
    setSelectedOptions({ ...queryOptions, inputPrice });
  }, []);

  const applyFilters = async () => {
    const { sort, direction } = router.query;
    const filterParams = filter(complement(isNil), { ...selectedOptions, sort, direction });
    const newQuery = stringify(filterParams, { arrayFormat: 'none', skipEmptyString: true });
    const newQueryParams = {
      pathname: router.pathname,
      query: filterParams,
    };

    if (isGroupingRoute) {
      const groupingSlug = router.query.grouping as string;
      await router.push(newQueryParams, `${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${groupingSlug}?${newQuery}`, {
        shallow: true,
      });
    } else {
      await router.push(newQueryParams, undefined, {
        shallow: true,
      });
    }

    window.scrollTo(0, 0);

    onClose();
  };

  const handleFilterSelection = (filterItem: string, value: string) => {
    const addFilterOption = (options: SelectedOptions) => {
      const newOptionValue = append(value, createArrayIfNot(options[filterItem]));
      return { ...options, [filterItem]: newOptionValue };
    };
    const removeFilterOption = (options: SelectedOptions) => {
      const newOptionValue = without([value], createArrayIfNot(options[filterItem]));
      return { ...options, [filterItem]: newOptionValue };
    };

    const parseNewFilters =
      // @ts-ignore
      ifElse(
        // @ts-ignore
        () => includes(value, selectedOptions[filterItem]),
        removeFilterOption,
        addFilterOption
      );

    // @ts-ignore
    const newFilters: SelectedOptions = selectedOptions[filterItem]
      ? parseNewFilters(selectedOptions)
      : addFilterOption(selectedOptions);

    if (newFilters.price?.length) {
      setSelectedOptions(omit(['inputPrice'], newFilters));
      return;
    }

    setSelectedOptions(newFilters);
  };

  const handleFilterInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.currentTarget;
    const newOptions = { ...selectedOptions };
    if (!newOptions.inputPrice) {
      newOptions.inputPrice = name === MIN_PRICE ? `${value}_` : `_${value}`;
    }
    if (name === MIN_PRICE) {
      newOptions.inputPrice = newOptions.inputPrice.replace(/.*_/g, `${value}_`);
    }
    if (name === MAX_PRICE) {
      newOptions.inputPrice = newOptions.inputPrice.replace(/_.*/g, `_${value}`);
    }

    if (newOptions.inputPrice === '_') {
      setSelectedOptions(omit(['inputPrice'], newOptions));
      return;
    }

    setSelectedOptions(omit(['price'], newOptions));
  };

  const handleResetFilterClick = () => {
    setSelectedOptions({});
  };

  const handleFilterClick = (name: string) => ifElse(isFilterOpen, () => setOpenOption(''), setOpenOption)(name);

  const renderDominantColor = (filterItem: string, option: FilterOption, index: number, parentIndex: number) => {
    const isSelected = includes(option.value, propOr([], filterItem, selectedOptions));

    return (
      <OptionColor
        key={index}
        title={option.value.replace('_', ' ')}
        selected={isSelected}
        onClick={() => handleFilterSelection(filterItem, option.value)}
        autoFocus={!index && !parentIndex}
      >
        <Color color={FILTER_DOMINANT_COLORS[option.value]} />
      </OptionColor>
    );
  };

  const renderDefaultFilter = (filterItem: string, option: FilterOption, index: number, parentIndex: number) => {
    const selectedFilters = pipe(propOr([], filterItem), map(ifElse(is(String), identity, toString)))(selectedOptions);
    const isSelected = includes(option.value.toString(), selectedFilters);

    return (
      <Option
        key={index}
        onClick={() => handleFilterSelection(filterItem, option.value)}
        autoFocus={!index && !parentIndex}
        aria-label={`Filter by ${option.name}`}
      >
        <OptionText selected={isSelected}>{option.name}</OptionText>
      </Option>
    );
  };

  const renderFilterOption = (filterItem: string, parentIndex: number) => (option: FilterOption, index: number) =>
    ifElse(
      equals(FILTER_NAME.DOMINANT_COLOR),
      () => renderDominantColor(filterItem, option, index, parentIndex),
      () => renderDefaultFilter(filterItem, option, index, parentIndex)
    )(filterItem);

  const renderFilterOptions = (filterItem: FilterItem, parentIndex: number) =>
    ifElse(
      equals(FILTER_NAME.DOMINANT_COLOR),
      () => (
        <FilterColorsContainer>
          {filterItem.options.map((option, index) => renderDominantColor(filterItem.name, option, index, parentIndex))}
        </FilterColorsContainer>
      ),
      () => (
        <FilterOptionsContainer>
          {filterItem.options.map((option, index) => renderDefaultFilter(filterItem.name, option, index, parentIndex))}
          {renderPriceInputs(filterItem.name === 'price')}
        </FilterOptionsContainer>
      )
    )(filterItem.name);

  const renderPriceInputs = renderWhenTrue(() => {
    const [minPrice, maxPrice] = selectedOptions.inputPrice ? selectedOptions.inputPrice.split('_') : ['', ''];

    return (
      <PriceInputs>
        <InputField
          name={MIN_PRICE}
          label={minLabel}
          type="number"
          pattern="[0-9]*"
          value={minPrice || ''}
          onChange={handleFilterInput}
        />
        <InputField
          name={MAX_PRICE}
          label={maxLabel}
          pattern="[0-9]*"
          type="number"
          value={maxPrice || ''}
          onChange={handleFilterInput}
        />
      </PriceInputs>
    );
  });

  const renderFilterItem = (filterItem: FilterItem, index: number) => {
    return (
      <FilterItemContainer key={index}>
        <FilterName>
          <FormattedMessage
            id={`filterName.${filterItem.name}`}
            defaultMessage={DEFAULT_FILTER_NAMES[filterItem.name]}
          />
          :
        </FilterName>
        {renderFilterOptions(filterItem, index)}
      </FilterItemContainer>
    );
  };

  const renderMobileFilterItem = (filterItem: FilterItem, index: number) => {
    return (
      <FilterItemMobileContainer key={index}>
        <FilterMobileName open={isFilterOpen(filterItem.name)} onClick={() => handleFilterClick(filterItem.name)}>
          <FormattedMessage
            id={`filterName.${filterItem.name}`}
            defaultMessage={DEFAULT_FILTER_NAMES[filterItem.name]}
          />
          :
        </FilterMobileName>
        <FilterOptionsMobileContainer open={isFilterOpen(filterItem.name)}>
          {filterItem.options.map(renderFilterOption(filterItem.name, index))}
          <FilterSpacer />
        </FilterOptionsMobileContainer>
      </FilterItemMobileContainer>
    );
  };

  const renderDesktopFilter = () => (
    <DesktopFiltersContainer>
      <FiltersContainer>{orderedOptions?.map(renderFilterItem)}</FiltersContainer>
    </DesktopFiltersContainer>
  );

  const renderMobileFilter = () => (
    <MobileFiltersContainer>
      <FiltersContainer>{orderedOptions?.map(renderMobileFilterItem)}</FiltersContainer>
    </MobileFiltersContainer>
  );

  const renderContent = renderWhenTrue(
    always(
      <TransformableContainer
        data-testid={FILTER_CONTAINER_TEST_ID}
        key="transformableContainer"
        initial={{ y: '-100%' }}
        animate={{ y: 0, transition: { duration: 0.6 } }}
        exit={{ y: '-100%', transition: { duration: 0.5 } }}
        transition={{ ease: 'ease-in-out' }}
      >
        <FocusTrap onFocus={handleOnClose} aria-label={closeLabel} />
        <FilterTitle>
          <FormattedMessage id="headerFilter.title" defaultMessage="FILTERS:" />
        </FilterTitle>
        {renderDesktopFilter()}
        {renderMobileFilter()}
        <FilterActions>
          <ActionButton
            hide={all(isEmpty, values(selectedOptions))}
            onClick={handleResetFilterClick}
            size={BUTTON_SIZE.M}
            fullWidth
          >
            <FormattedMessage id="headerFilter.reset" defaultMessage="Reset Filters" />
          </ActionButton>
          <ActionButton onClick={applyFilters} size={BUTTON_SIZE.M} inverted fullWidth>
            <FormattedMessage id="headerFilter.apply" defaultMessage="Apply Filters" />
          </ActionButton>
        </FilterActions>
        <CloseButton onClick={handleOnClose} aria-label={closeLabel} />
        <FocusTrap onFocus={handleOnClose} />
      </TransformableContainer>
    )
  );

  return (
    <Container open={open}>
      <AnimatePresence>{renderContent(open)}</AnimatePresence>
    </Container>
  );
};
